import React, { useState, useEffect, useRef, useMemo } from "react";
import Chip from "@mui/material/Chip";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import PeopleIcon from "@mui/icons-material/People";
import SearchIcon from "@mui/icons-material/Search";

import { API_get_role_auto_enroll_count } from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";

function RoleAssignmentChips({
  roleNames,
  selectedRoleIds,
  onChange,
  companyId,
}) {
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [search, setSearch] = useState("");
  const [count, setCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);

  // Token guards against an in-flight count from an older selection
  // overwriting a fresher one if requests resolve out of order.
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!companyId) return;
    if (!selectedRoleIds || selectedRoleIds.length === 0) {
      setCount(0);
      setLoadingCount(false);
      return;
    }

    setLoadingCount(true);
    const timer = setTimeout(async () => {
      const myReqId = ++reqIdRef.current;
      try {
        const res = await getRightworkAPI(API_get_role_auto_enroll_count, {
          company_id: companyId,
          role_ids: selectedRoleIds.join(","),
        });
        if (reqIdRef.current === myReqId) {
          setCount(res.data?.redux?.payload?.count ?? 0);
          setLoadingCount(false);
        }
      } catch (_) {
        if (reqIdRef.current === myReqId) {
          setLoadingCount(false);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedRoleIds, companyId]);

  const selectedRoles = useMemo(
    () =>
      (roleNames || []).filter((r) => (selectedRoleIds || []).includes(r.id)),
    [roleNames, selectedRoleIds]
  );

  const availableRoles = useMemo(() => {
    const taken = new Set(selectedRoleIds || []);
    const term = search.trim().toLowerCase();
    return (roleNames || [])
      .filter((r) => !taken.has(r.id))
      .filter((r) => !term || r.role_name.toLowerCase().includes(term));
  }, [roleNames, selectedRoleIds, search]);

  const handleAddRole = (roleId) => {
    onChange([...(selectedRoleIds || []), roleId]);
  };

  const handleRemoveRole = (roleId) => {
    onChange((selectedRoleIds || []).filter((id) => id !== roleId));
  };

  const closePopover = () => {
    setPopoverAnchor(null);
    setSearch("");
  };

  const countLabel = (() => {
    if (!selectedRoleIds || selectedRoleIds.length === 0) return null;
    if (loadingCount && count === null) return "Calculating impact…";
    if (count === null) return null;
    return `${count} ${count === 1 ? "employee" : "employees"} will auto-enroll`;
  })();

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <PeopleIcon className="text-slate-500" style={{ fontSize: "18px" }} />
        <label className="block text-sm font-medium text-slate-700">
          Auto-assign to Roles
        </label>
      </div>

      <div className="border border-slate-200 rounded-lg p-2 flex flex-wrap gap-1.5 items-center bg-white min-h-[44px]">
        {selectedRoles.map((r) => (
          <Chip
            key={r.id}
            label={r.role_name}
            onDelete={() => handleRemoveRole(r.id)}
            size="small"
            sx={{
              backgroundColor: "#f5f3ff",
              color: "#6d28d9",
              fontWeight: 500,
              "& .MuiChip-deleteIcon": {
                color: "#6d28d9",
                "&:hover": { color: "#5b21b6" },
              },
            }}
          />
        ))}
        <button
          onClick={(e) => setPopoverAnchor(e.currentTarget)}
          className="flex items-center gap-1 px-2 py-1 text-xs text-violet-700 hover:bg-violet-50 rounded transition-colors"
        >
          <AddIcon style={{ fontSize: "16px" }} />
          {selectedRoles.length === 0 ? "Add role" : "Add"}
        </button>
      </div>

      {countLabel && (
        <p className="text-xs text-slate-500 mt-2">{countLabel}</p>
      )}

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            width: 280,
            maxHeight: 360,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <div className="px-3 py-2 border-b border-slate-200">
          <TextField
            placeholder="Search roles…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  style={{ fontSize: "18px", marginRight: 6, color: "#94a3b8" }}
                />
              ),
              sx: { fontSize: "0.875rem" },
            }}
          />
        </div>
        <div className="overflow-y-auto py-1">
          {availableRoles.length === 0 ? (
            <p className="text-xs text-slate-500 px-3 py-3 text-center">
              {selectedRoles.length === (roleNames?.length || 0)
                ? "All roles are already assigned."
                : "No matching roles."}
            </p>
          ) : (
            availableRoles.map((r) => (
              <button
                key={r.id}
                onClick={() => handleAddRole(r.id)}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-violet-50 transition-colors"
              >
                {r.role_name}
              </button>
            ))
          )}
        </div>
      </Popover>
    </div>
  );
}

export default RoleAssignmentChips;
